// Import navigation hook from React Router
import { useNavigate } from "react-router-dom";

// Import components
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import HelpButton from "global/components/HelpButton";

// Import utility functions
import { formatDisplayDateTime } from "global/utilities/ConvertDate";
import { ToTitleCase } from "global/utilities/ConvertCasing";
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import css
import "./SubscriptionSummaryPanel.css";



const getBillingPeriodLabel = (subscriptionPackage = "") => {

    // Return monthly label
    if (subscriptionPackage.includes("_monthly")) {

        return "Monthly";

    }

    // Return yearly label
    if (subscriptionPackage.includes("_yearly")) {

        return "Yearly";

    }

    // Return empty label
    return "";

};



const SubscriptionSummaryPanel = ({
    userData,
    subscriptionLevels = [],
    subscriptionPackages = [],
    showStatus = false,
    showAutoRenew = false,
    onToggleAutoRenew,
    buttonTitle = "Upgrade",
    buttonSize = "l",
    showDescription = true,
    smallRenewDisplay = false,
    showHelp = true,
}) => {

    // Get the navigate function for programmatic navigation
    const navigate = useNavigate();

    // Get active subscription safely
    const activeSubscription = userData?.active_subscription || {};

    // Get current subscription level
    const subscriptionLevel = activeSubscription?.subscription_level || "free";

    // Check whether the current subscription is free
    const currentSubscriptionLevelIsFree = subscriptionLevel === "free";

    // Get package code
    const subscriptionPackage = activeSubscription?.subscription_package || "";

    // Get billing period label
    const billingPeriodLabel = getBillingPeriodLabel(subscriptionPackage);

    // Get the matching level from subscription levels
    const matchingSubscriptionLevel = subscriptionLevels?.find((level) => level.code === subscriptionLevel) || null;

    // Get the matching package from subscription packages
    const matchingSubscriptionPackage = subscriptionPackages?.find((packageRow) => packageRow.code === subscriptionPackage) || null;

    // Check whether a higher package is available
    const hasAvailableUpgrades = activeSubscription?.available_upgrades?.length > 0;

    // Monthly users should still be able to go to Plans to switch to yearly
    const canSwitchMonthlyToYearly = subscriptionPackage.includes("_monthly");

    // Check whether the plans button should be shown
    const showPlansButton = hasAvailableUpgrades || canSwitchMonthlyToYearly;

    // Get status class
    const statusClassName = activeSubscription?.status === "active" ? "active" : "inactive";

    // Render subscription summary
    return (

        <div className="subscription-summary-panel">

            <div className="subscription-summary-panel__header">

                <h3 className="subscription-summary-panel__title">

                    Subscription {showHelp && <HelpButton section="account" code="subscription" label="Your Subscription" />}

                </h3>

                {showStatus && !currentSubscriptionLevelIsFree && (

                    <span className={`subscription-summary-panel__status-badge subscription-summary-panel__status-badge--${statusClassName}`}>

                        {ToTitleCase(activeSubscription?.status || "")}

                    </span>

                )}

            </div>

            <div className="subscription-summary-panel__plan-row">

                <span className={`subscription-summary-panel__plan-dot subscription-summary-panel__plan-dot--${subscriptionLevel}`} />

                <h3 className="subscription-summary-panel__plan-name">

                    {ToTitleCase(subscriptionLevel)}

                </h3>

                {billingPeriodLabel && (

                    <span className="subscription-summary-panel__billing-period">

                        ({billingPeriodLabel})

                    </span>

                )}

            </div>

            <p className="subscription-summary-panel__meta">

                <span>Current level </span>

                {activeSubscription?.starts_at && (

                    <> 

                        • Since {formatDisplayDateTime(activeSubscription.starts_at, false)}

                        {smallRenewDisplay &&

                            (activeSubscription?.auto_renew === "Y" ? (

                                 <span> • Renews on {formatDisplayDateTime(activeSubscription?.ends_at, false)} at {FormatCurrency(userData.currency, matchingSubscriptionPackage?.price_inc_tax)}</span>

                            ) : (

                                <span> • Ends on {formatDisplayDateTime(activeSubscription?.ends_at, false)}</span>

                            )
                            
                        )}
                        
                    </> 

                    

                )}

            </p>

            {showDescription && matchingSubscriptionLevel?.description && (

                <p className="subscription-summary-panel__description">

                    {matchingSubscriptionLevel.description}

                </p>

            )}

            {showAutoRenew && !currentSubscriptionLevelIsFree && (

                <div className="subscription-summary-panel__auto-renew">

                    <div className="subscription-summary-panel__auto-renew-row">

                        Auto Renew

                        <ToggleSwitch 
                            value={activeSubscription?.auto_renew === "Y" ? true : false}
                            size="s"
                            onChange={(event) => onToggleAutoRenew?.(event.target.value)} 
                        />

                    </div>

                    <span className="subscription-summary-panel__renewal-line">

                        {activeSubscription?.auto_renew === "Y" ? (

                            <>Renews on {formatDisplayDateTime(activeSubscription?.ends_at, false)} at {FormatCurrency(userData.currency, matchingSubscriptionPackage?.price_inc_tax)}</>

                        ) : (

                            <>Ends on {formatDisplayDateTime(activeSubscription?.ends_at, false)}</>

                        )}

                    </span>

                </div>

            )}

            {showPlansButton && (

                <div className="subscription-summary-panel__button-row">

                    <CreateButton 
                        title={buttonTitle} 
                        size={buttonSize} 
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate("/plans");
                        }} 
                    />

                </div>

            )}

        </div>

    );

};

export default SubscriptionSummaryPanel;